import { useState } from "react";

import { useRouteTimeline } from "@web-app/api";
import { LoadingIcon } from "@web-app/components";
import { Order, Route } from "@web-app/types";
import { getTimezoneForRoute } from "@web-app/utils/helpers";

import RouteAssigneeActivity from "./RouteAssigneeActivity";
import RouteCopyActivity from "./RouteCopyActivity";
import RouteCreationActivity from "./RouteCreationActivity";
import RouteDispatchActivity from "./RouteDispatchActivity";
import RouteOptimizeClickActivity from "./RouteOptimizeClickActivity";
import RouteOrdersActivity from "./RouteOrdersActivity";
import RoutePickupTimeActivity from "./RoutePickupTimeActivity";
import RouteRenamedActivity from "./RouteRenamedActivity";

type RouteTimelinePropsTypes = {
  orders: Order[];
  route: Route;
};

export default function RouteTimeline({
  route,
  orders,
}: RouteTimelinePropsTypes) {
  const [timelineLimit] = useState(10);

  const { data: timelineData, isSuccess } = useRouteTimeline({
    routeId: route.id,
    timelineLimit,
  });

  const timelineTotalLength = timelineData?.length || 0;
  const timezone = getTimezoneForRoute(orders);

  if (!isSuccess) {
    return (
      <div className="flex items-center justify-center mt-5 gap-2">
        <LoadingIcon className="w-3" />
        <span className="text-xs">Loading timeline...</span>
      </div>
    );
  }

  if (isSuccess && timelineTotalLength === 0) {
    return (
      <div className="flex items-center justify-center mt-5 gap-2">
        <span className="text-sm">No timeline activities found.</span>
      </div>
    );
  }

  return (
    <>
      <ul role="list" className="space-y-6">
        {timelineData?.map((item, itemIdx) => {
          const showTimelineBar = itemIdx !== timelineTotalLength - 1;

          switch (item.entity) {
            case "assigneeId":
              return (
                <RouteAssigneeActivity
                  key={item.id}
                  item={item}
                  showTimelineBar={showTimelineBar}
                  timezone={timezone}
                />
              );
            case "estimatedPickupTime":
              return (
                <RoutePickupTimeActivity
                  key={item.id}
                  item={item}
                  showTimelineBar={showTimelineBar}
                  timezone={timezone}
                />
              );
            case "optimizeClicked":
              return (
                <RouteOptimizeClickActivity
                  key={item.id}
                  item={item}
                  showTimelineBar={showTimelineBar}
                  timezone={timezone}
                />
              );
            case "ordersOrder":
              return (
                <RouteOrdersActivity
                  key={item.id}
                  item={item}
                  showTimelineBar={showTimelineBar}
                  timezone={timezone}
                />
              );
            case "externalId":
              if (item.action === "UPDATE") {
                return (
                  <RouteRenamedActivity
                    key={item.id}
                    item={item}
                    showTimelineBar={showTimelineBar}
                    timezone={timezone}
                  />
                );
              }
              return null;
            case "route":
              if (item.action === "CREATE") {
                return (
                  <RouteCreationActivity
                    key={item.id}
                    item={item}
                    showTimelineBar={showTimelineBar}
                    timezone={timezone}
                  />
                );
              }
              if (item.action === "COPY") {
                return (
                  <RouteCopyActivity
                    key={item.id}
                    item={item}
                    showTimelineBar={showTimelineBar}
                    timezone={timezone}
                  />
                );
              }
              return null;
            case "dispatchLogic":
              return (
                <RouteDispatchActivity
                  key={item.id}
                  item={item}
                  showTimelineBar={showTimelineBar}
                  timezone={timezone}
                />
              );
            default:
              return null;
          }
        })}
      </ul>
    </>
  );
}
